import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { LogIn, LogOut, UserRound } from 'lucide';

// MUI
import Box, { BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

// common
import Icon from 'common/components/Icon';
import useGeneralHook from 'common/hook/useGeneralHook';
import useIsGuest from 'common/hook/useIsGuest';
import { logoutFnc } from '@redux/authReducer';
import { AppState } from '@redux/store';
import { ROUTES_PATH } from 'layout/constants';
import ThemeSwitch from 'layout/main/ThemeSwitch';
// import LanguageSelect from "intl/component/LanguageSelect";

interface Props {
  sx?: BoxProps['sx'];
}

const ProfileBox = (props: Props) => {
  const { sx } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { dispatch, isMobile } = useGeneralHook();
  const userInfo = useSelector((state: AppState) => state.auth.userInfo);
  const isGuest = useIsGuest();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'userInfo-popover' : undefined;

  const logout = () => {
    dispatch(logoutFnc());
  };

  const name = useMemo(() => {
    if (!userInfo || !userInfo.userAuthen) return '';

    const { userAuthen } = userInfo;
    const emailOrName = userAuthen.email || userAuthen.unverifiedEmail || userInfo.name;

    return emailOrName || 'N/A';
  }, [userInfo]);

  const label = isGuest ? '' : name;
  const initial = (isGuest ? 'K' : label.trim().charAt(0) || 'G').toUpperCase();

  return (
    <>
      <Button
        variant="text"
        color="inherit"
        className="items-center shrink-0 flex"
        sx={{
          gap: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          textAlign: 'start',
          borderRadius: 999,
          px: 1,
          py: 0.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'grey.50' },
          ...sx,
        }}
        onClick={handleClick}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initial}
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', pr: 0.5 }}>
          <Typography variant="body2" className="line-clamp-1" sx={{ fontWeight: 600 }}>
            {isGuest ? <FormattedMessage id="guest" /> : name}
          </Typography>
        </Box>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={
          isMobile
            ? {
                vertical: 'top',
                horizontal: 'center',
              }
            : { vertical: 'bottom', horizontal: 'center' }
        }
        transformOrigin={
          isMobile
            ? {
                vertical: 'bottom',
                horizontal: 'center',
              }
            : undefined
        }
        className="mb-4 tablet:mt-2"
        PaperProps={{
          variant: 'outlined',
          elevation: 0,
          sx: {
            width: 280,
            p: 1,
            gap: 0.5,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            boxShadow: '0 12px 32px rgba(14, 23, 38, 0.12)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, pt: 1, pb: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initial}
          </Box>
          <Typography variant="body2" className="line-clamp-1" sx={{ fontWeight: 600, minWidth: 0 }}>
            {isGuest ? <FormattedMessage id="guest" /> : name}
          </Typography>
        </Box>
        <Divider sx={{ mx: -1, mb: 0.5 }} />
        {/* Khách: BE 403 mọi trang thông tin tài khoản (không nằm trong allowlist) — bỏ mục hồ sơ. */}
        {!isGuest && (
          <ButtonBase
            component={RouterLink}
            to={`/${ROUTES_PATH.profile}`}
            onClick={handleClose}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.25,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              color: 'text.primary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Icon icon={UserRound} size={16} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <FormattedMessage id="user.title" />
            </Typography>
          </ButtonBase>
        )}
        {/* <LanguageSelect /> */}
        <ThemeSwitch />
        <Divider sx={{ mx: -1, my: 0.5 }} />
        {/* Khách chưa có tài khoản nào để "đăng xuất" — việc họ cần là ĐĂNG NHẬP. Hành động vẫn là
            logoutFnc: xoá cookie khách + authOut() để ProtectedRoute tự đưa về /login (giống CTA ở
            Quick Search); nếu điều hướng tay sang /login thì RedirectRoute thấy isLogin vẫn true
            của token khách và đẩy ngược lại. */}
        <Button
          onClick={logout}
          fullWidth
          variant={isGuest ? 'contained' : 'outlined'}
          color={isGuest ? 'primary' : 'error'}
          startIcon={<Icon icon={isGuest ? LogIn : LogOut} size={16} />}
        >
          <FormattedMessage id={isGuest ? 'login' : 'logout'} />
        </Button>
      </Popover>
    </>
  );
};
export default ProfileBox;
